import { APIEntityType } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { Icon, IconName } from '@ardoq/icons';
import { Box, Stack } from '@ardoq/layout';
import { WithPopover } from '@ardoq/popovers';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import {
  SidebarMenu,
  SidebarMenuMenu,
  SidebarMenuSection,
} from '@ardoq/sidebar-menu';
import { Paragraph } from '@ardoq/typography';
import { trackAuditLogEntryPoint } from 'auditLog/tracking';
import { navigateToAuditLog } from 'router/navigationActions';
import {
  deleteReference,
  lockReferences,
  reverseReference,
  unlockReferences,
} from 'streams/references/ReferenceActions';
import { editReference, selectReference } from './actions';
import {
  ReferenceModel,
  ReferenceOptionsState,
  referenceOptions$,
} from './referenceOptions$';
import { t, type I18nLocale } from '@ardoq/i18n';

type ReferenceLinkProps = {
  readonly reference: ReferenceModel;
};
const ReferenceLink = ({ reference }: ReferenceLinkProps) => (
  <>
    <span>{reference.leftName}</span>
    {reference.arrowDirection === 'right' ? (
      <Icon
        iconName={IconName.ARROW_RIGHT_ALT}
        style={{ verticalAlign: 'middle', lineHeight: '14px' }}
      />
    ) : (
      <Icon
        iconName={IconName.ARROW_RIGHT_ALT}
        style={{
          verticalAlign: 'middle',
          lineHeight: '14px',
          rotate: '180deg',
        }}
      />
    )}
    <span>{reference.rightName}</span>
  </>
);

type ReverseReferenceConstraintMessageProps = {
  readonly message: string;
  readonly isReverseBlocked: boolean;
  readonly isReverseWarning: boolean;
  readonly i18nLocale: I18nLocale;
};

const ReverseReferenceConstraintMessage = ({
  message,
  isReverseBlocked,
  isReverseWarning,
  i18nLocale,
}: ReverseReferenceConstraintMessageProps) => (
  <Stack gap="xsmall">
    {isReverseBlocked && (
      <Paragraph variant="text2Bold">
        {t(
          i18nLocale,
          'menus:referenceOptions.constraint.reverseBlocked',
          'Reverse is blocked'
        )}
      </Paragraph>
    )}
    {isReverseWarning && (
      <>
        <Paragraph variant="text2Bold">
          {t(
            i18nLocale,
            'menus:referenceOptions.constraint.violatesMetamodel',
            'Violates metamodel constraints'
          )}
        </Paragraph>
        <Paragraph variant="caption">
          {t(
            i18nLocale,
            'menus:referenceOptions.constraint.reverseWarning',
            'Reversing this reference would create a relationship that is not allowed by the metamodel.'
          )}
        </Paragraph>
      </>
    )}
    <Paragraph variant="caption">{message}</Paragraph>
  </Stack>
);

const ReferenceOptionsBase = ({
  hasWorkspaceWriteAccess,
  canEditReference,
  canEditReferencesTargetComponent,
  isViewVersionHistoryDisabled,
  references,
  contextReferenceId,
  locked,
  toggleLock,
  reverseConstraintViolation,
  i18nLocale,
}: ReferenceOptionsState) => {
  if (!hasWorkspaceWriteAccess && !canEditReference) {
    <SidebarMenu
      emptyContentMessage={t(
        i18nLocale,
        'menus:referenceOptions.error.noEditAccess',
        'You must have edit access in the reference source workspace to edit a reference.'
      )}
    />;
  }
  if (!contextReferenceId && !references.length) {
    return (
      <SidebarMenu
        emptyContentMessage={t(
          i18nLocale,
          'menus:referenceOptions.error.selectComponent',
          'In order for references to appear here, please select a component.'
        )}
      />
    );
  }
  if (!contextReferenceId) {
    return (
      <SidebarMenu>
        <SidebarMenuSection
          title={t(
            i18nLocale,
            'menus:referenceOptions.section.selectReference',
            'Please select a reference'
          )}
          isCollapsible={false}
        >
          <SidebarMenuMenu
            options={references.map(reference => {
              return {
                label: <ReferenceLink reference={reference} />,
                onClick: () => dispatchAction(selectReference(reference._id)),
                dataClickId: `select-reference-${reference._id}-menu-item`,
              };
            })}
          />
        </SidebarMenuSection>
      </SidebarMenu>
    );
  }

  const ReverseReferencePopoverContent = reverseConstraintViolation.violates
    ? () => (
        <ReverseReferenceConstraintMessage
          message={reverseConstraintViolation.message}
          isReverseBlocked={reverseConstraintViolation.isBlocked}
          isReverseWarning={reverseConstraintViolation.isWarning}
          i18nLocale={i18nLocale}
        />
      )
    : undefined;

  return (
    <SidebarMenu>
      <SidebarMenuSection
        title={t(
          i18nLocale,
          'menus:referenceOptions.section.editReference',
          'Edit reference'
        )}
        isCollapsible={false}
      >
        <SidebarMenuMenu
          options={[
            {
              label: t(
                i18nLocale,
                'menus:referenceOptions.editReferenceProperties',
                'Edit reference properties'
              ),
              onClick: () => dispatchAction(editReference(contextReferenceId)),
              isDisabled: locked,
              dataClickId: 'edit-reference-properties-menu-item',
            },
            {
              label: t(
                i18nLocale,
                'menus:referenceOptions.deleteReference',
                'Delete reference'
              ),
              onClick: () =>
                dispatchAction(deleteReference(contextReferenceId)),
              isDisabled: locked,
              dataClickId: 'delete-reference-menu-item',
            },
            canEditReferencesTargetComponent && {
              label: (
                <WithPopover content={ReverseReferencePopoverContent}>
                  <Box width="full" height="full">
                    {t(
                      i18nLocale,
                      'menus:referenceOptions.reverseReference',
                      'Reverse reference'
                    )}
                  </Box>
                </WithPopover>
              ),
              onClick: () => {
                dispatchAction(reverseReference(contextReferenceId));
              },
              isDisabled: reverseConstraintViolation.isBlocked || locked,
              dataClickId: 'reverse-reference-menu-item',
            },
            toggleLock.canToggleLock && {
              label: locked
                ? t(
                    i18nLocale,
                    'menus:referenceOptions.unlockReference',
                    'Unlock reference'
                  )
                : t(
                    i18nLocale,
                    'menus:referenceOptions.lockReference',
                    'Lock reference'
                  ),
              onClick: () =>
                locked
                  ? dispatchAction(unlockReferences([contextReferenceId]))
                  : dispatchAction(
                      lockReferences({
                        referenceIds: [contextReferenceId],
                        userId: toggleLock.currentUserId,
                      })
                    ),
              dataClickId: 'toggle-lock-reference-menu-item',
            },
          ].filter(isTruthy)}
        />
      </SidebarMenuSection>
      <SidebarMenuSection
        title={t(
          i18nLocale,
          'menus:referenceOptions.section.actions',
          'Actions'
        )}
        isCollapsible={false}
      >
        <SidebarMenuMenu
          options={[
            {
              label: t(
                i18nLocale,
                'menus:referenceOptions.viewReferenceHistory',
                'View reference history'
              ),
              onClick: () => {
                trackAuditLogEntryPoint('reference history');
                dispatchAction(
                  navigateToAuditLog({
                    entities: [{ id: contextReferenceId }],
                    entityType: APIEntityType.REFERENCE,
                  })
                );
              },
              isDisabled: isViewVersionHistoryDisabled,
              dataClickId: 'view-reference-history-menu-item',
            },
          ]}
        />
      </SidebarMenuSection>
    </SidebarMenu>
  );
};

export const ReferenceOptions = connect(
  ReferenceOptionsBase,
  referenceOptions$
);
